<template>
  <div class="flex flex-col h-full">
    <h2
      class="uppercase text-lg mb-6 py-4 text-left font-semibold"
    >Select the videoette to merge with your video:</h2>

    <!-- start of filters -->
    <div class="flex flex-col md:flex-row mb-3 border-b py-5">
      <div
        class="bg-white md:w-1/4 w-full h-full mb-6 md:mb-0 px-3 flex items-center self-end border rounded overflow-hidden md:mr-5 shadow"
      >
        <i class="fa fa-search text-grey"></i>
        <input
          v-model="search"
          type="text"
          class="py-4 px-2 w-full h-full bg-transparent"
          placeholder="Search"
        />
      </div>

      <div class="flex w-full md:w-1/4 mb-8 md:mb-0 md:border-r md:pr-10">
        <div class="flex w-full flex-col lg:mr-3">
          <label for="include" class="mb-2 text-xs">Status:</label>
          <pl-select
            v-model="status"
            name="filter_menu"
            id="include"
            select-class="bg-white rounded h-10 py-0 shadow text-sm"
            class="lg:w-48 w-full"
          >
            <option :value="null">All</option>
            <option value="queued">Queued</option>
            <option value="complete">Publishing</option>
            <option value="published">Published</option>
            <option value="excluded">Cancelled</option>
            <option value="rejected">Rejected</option>
          </pl-select>
        </div>
      </div>
      <div class="flex items-center justify-center md:pl-10">
        <div class="mr-6">
          <input id="sales_filter" type="checkbox" class="blue" v-model="sales" />
          <label for="sales_filter">Sales</label>
        </div>
        <div>
          <input id="lettings_filter" type="checkbox" class="blue" v-model="lettings" />
          <label for="lettings_filter">Lettings</label>
        </div>
      </div>
    </div>
    <!-- end of filters -->

    <div class="xl:hidden mx-auto mb-4 w-1/3 flex justify-around items-center text-sm text-grey-light">
      <i class="fa fa-arrow-left fa-xs"></i>
      scroll to see more
      <i class="fa fa-arrow-right fa-xs"></i>
    </div>

    <div class="w-full flex-1 overflow-x-scroll">
      <table class="w-full min-w-288">
        <thead>
          <tr class="text-xs uppercase">
            <th class="p-3 text-left">Property</th>
            <th class="p-3">Price</th>
            <th class="p-3">Department</th>
            <th class="p-3">Created</th>
            <th class="p-3">Status</th>
            <th class="p-3">Select</th>
          </tr>
        </thead>

        <tbody v-if="!loading && videoettes && videoettes.length > 0">
          <tr
            v-for="(videoette, index) in videoettes"
            :key="videoette.id"
            class="text-xs md:text-sm border-b text-center"
            :class="{
              'bg-grey-lightest': index % 2 === 0
            }"
          >
            <td
              class="p-3 font-bold border-l-2"
              :class="{
                'border-green': videoette.status === 'Published',
                'border-orange': videoette.status === 'Queued',
                'border-red-light': videoette.status === 'Rejected'
              }"
            >
              <div class="flex items-center text-left">
                <div
                  v-if="videoette.image"
                  class="w-16 h-10 bg-cover inline-block mr-2"
                  :style="{ 'backgroundImage': `url(${videoette.image})` }"
                ></div>
                <div class="flex flex-col">
                  <!-- <span class="font-normal text-xs mb-1" :title="videoette.display_address">{{ truncate(videoette.display_address, 24) }}</span> -->
                  <span
                    class="font-normal text-xs mb-1"
                    :title="videoette.display_address"
                  >{{ videoette.display_address }}</span>
                  <span
                    class="font-normal text-xs text-grey"
                  >{{ videoette.town }} {{ videoette.postcode }}</span>
                </div>
              </div>
            </td>
            <td class="p-3 font-bold">{{ videoette.price }}</td>
            <td class="p-3">{{ videoette.department }}</td>
            <td class="p-3 text-xs">
              <span class="block">{{ videoette.created_at.date }}</span>
              <span class="block mt-1">{{ videoette.created_at.time }}</span>
            </td>
            <td
              class="p-3 text-xs"
              :class="{
                'text-green': videoette.status === 'Published',
                'text-orange': videoette.status === 'Queued',
                'text-red-light': videoette.status === 'Rejected'
              }"
            >{{ videoette.status }}</td>
            <td
              class="p-3 border-r-2"
              :class="{
                'border-green': videoette.status === 'Published',
                'border-orange': videoette.status === 'Queued',
                'border-red-light': videoette.status === 'Rejected'
              }"
            >
              <button
                @click="sendValue(videoette)"
                class="uppercase px-4 py-2 bg-secondary rounded text-xs"
                :disabled="!videoette.rendered"
                :class="{ 'cursor-not-allowed': !videoette.rendered }"
              >Select</button>
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="loading">
          <tr>
            <td colspan="6" class="text-center pt-8 text-2xl text-grey-dark">
              <loader></loader>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="6" class="text-center pt-8 text-2xl text-grey-dark">No Videoettes Found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading && page < lastPage" class="w-full text-sm flex py-3 justify-center">
      <button @click="moreResults" class="border-b text-grey-dark">
        SHOW MORE
        <i class="fa fa-caret-down text-xs"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { truncate, moneyFormat } from '@/helpers'
import { mapActions, mapState } from 'vuex'
import { debounce } from 'lodash'
import axios from 'axios'

export default {
  name: 'VideoetteSelector',

  prop: ['value'],

  data () {
    return {
      loading: false,
      req: null,
      page: 1,
      lastPage: 1,
      search: '',
      status: null,
      sales: true,
      lettings: true
    }
  },

  computed: {
    ...mapState({
      office: state => state.selectedOffice,
      propertyVideos: state => state.videoettes.propertyVideos,
      videoettes: state => state.videoettes.propertyVideos.data
    })
  },

  methods: {
    ...mapActions(['getVideoettes']),

    truncate,

    moneyFormat,

    sendValue (val) {
      this.$emit('input', val)
      this.$parent.$emit('ChangeView', null)
    },

    moreResults () {
      this.page++
      this.loadVideoettes()
    },

    loadVideoettes () {
      this.loading = true

      if (this.req) {
        this.req.cancel()
      }
      this.req = axios.CancelToken.source()

      this.getVideoettes(
        {
          params: {
            office_id: this.office.id,
            page: this.page,
            q: this.search,
            status: this.status,
            sales: this.sales,
            lettings: this.lettings
          },
          cancelToken: this.req.token
        }
      ).then(response => {
        this.lastPage = response.data.meta.last_page
      }).finally(() => {
        this.loading = false
      })
    },

    loadVideoettesDelayed: debounce(function () {
      this.loadVideoettes()
    }, 500)
  },

  watch: {
    office () {
      this.page = 1
      this.loadVideoettes()
    },

    search () {
      this.page = 1
      this.loadVideoettesDelayed()
    },

    status () {
      this.page = 1
      this.loadVideoettesDelayed()
    },

    sales () {
      this.page = 1
      this.loadVideoettesDelayed()
    },

    lettings () {
      this.page = 1
      this.loadVideoettesDelayed()
    }
  },

  mounted () {
    this.loadVideoettes()
  }
}
</script>
